/**
 * AIF-01 — read the taxonomy admin error envelope off an axios-like error.
 *
 * The api-client rethrows with the original axios error on `.cause`, so the
 * HTTP status + JSON body live at `err.cause.response.{status,data}` (mirrors
 * `PublishDiffDialog.extractPublishIssues`). A 409 carries `{code:'conflict',
 * reason}`; AIF-01 reasons include `class_index_immutable` (the §3.1 backstop).
 */
export interface ApiErrorBody {
  code?: string;
  reason?: string;
  message?: string;
}

interface AxiosLikeError {
  status?: number;
  cause?: { response?: { status?: number; data?: unknown } };
}

export function errStatus(err: unknown): number | undefined {
  const e = err as AxiosLikeError;
  return e.status ?? e.cause?.response?.status;
}

export function errBody(err: unknown): ApiErrorBody | undefined {
  const e = err as AxiosLikeError;
  return e.cause?.response?.data as ApiErrorBody | undefined;
}

/** Human-readable message for a 409 reason, else a generic fallback. */
export function conflictMessage(err: unknown, fallback: string): string {
  const body = errBody(err);
  switch (body?.reason) {
    case "model_name_exists":
      return "A model with that name already exists.";
    case "version_number_exists":
      return "That version number already exists for this model.";
    case "class_index_exists":
      return "Another class already uses that index.";
    case "class_name_exists":
      return "Another class already uses that name.";
    case "class_index_immutable":
      return "Index is locked — findings reference this class. Re-train with changed indices ⇒ create a new model.";
    default:
      return fallback;
  }
}
